import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { deepCompare } from '/packages/shared-components/utils/util';

import { DeviceStatus } from '~/src/generated/portal/graphql';
import { getDevicesQuery } from '~src/generated/portal/graphql';
import GET_DEVICES from '~src/queries/codegen/getDevices';

type DeviceFromQuery = getDevicesQuery['devices'][0];

const KEY = 'selected_device_id';

const initialContext = {
  selectedDevice: undefined,
  setSelectedDevice: () => null,

  setSelectedDeviceId: () => null,
  selectedDeviceId: sessionStorage.getItem(KEY) || undefined,

  reservedDevices: [],
  setReservedDevices: () => null,
};

interface UseSelectedDeviceContextProps {
  selectedDevice: DeviceFromQuery | undefined;
  setSelectedDevice: (value: DeviceFromQuery | undefined) => void;

  setSelectedDeviceId: (val: string | undefined) => void;
  selectedDeviceId?: string;

  setReservedDevices: (val: getDevicesQuery['devices']) => void;
  reservedDevices: getDevicesQuery['devices'];
}

export const useSelectedDevice = (projectId: string) => {
  const {
    setSelectedDeviceId,
    selectedDeviceId,
    selectedDevice,
    setSelectedDevice,
    reservedDevices,
    setReservedDevices,
  } = useContext(UseSelectedDeviceContext);

  const { data, loading, error } = useQuery(GET_DEVICES, {
    variables: { projectId },
  });

  useEffect(() => {
    if (data) {
      const newSelectedDevice = data.devices.find(
        ({ id, status }) =>
          id === selectedDeviceId &&
          (status === DeviceStatus.Not_Connected_Reserved_By_You || status === DeviceStatus.Connected_Reserved_By_You),
      );

      if (!deepCompare(selectedDevice, newSelectedDevice)) setSelectedDevice(newSelectedDevice);

      const newDevices = data.devices.map((e) => JSON.stringify(e));
      const oldDevices = reservedDevices.map((e) => JSON.stringify(e));

      const newReservedDevices = data.devices.filter(
        ({ status }) =>
          status === DeviceStatus.Not_Connected_Reserved_By_You || status === DeviceStatus.Connected_Reserved_By_You,
      );

      if (
        (!isContained(newDevices, oldDevices) || !isContained(oldDevices, newDevices)) &&
        !deepCompare(reservedDevices, newReservedDevices)
      )
        setReservedDevices(newReservedDevices);
    }
  }, [data]);

  const onSelectDevice = useCallback(
    (selectedDeviceId?: string) => {
      if (selectedDeviceId) {
        sessionStorage.setItem(KEY, selectedDeviceId);
        setSelectedDeviceId(selectedDeviceId);
      } else {
        sessionStorage.removeItem(KEY);
        setSelectedDeviceId(undefined);
      }
    },
    [setSelectedDeviceId],
  );

  return { loading, error, reservedDevices, selectedDevice, onSelectDevice };
};

const isContained = (src: string[], target: string[]) => {
  for (const el of src) {
    if (!target.includes(el)) return false;
  }
  return true;
};

export const UseSelectedDeviceContext = React.createContext<UseSelectedDeviceContextProps>(initialContext);

export const SelectedDeviceProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(initialContext.selectedDeviceId);
  const [selectedDevice, setSelectedDevice] = useState<DeviceFromQuery | undefined>(initialContext.selectedDevice);
  const [reservedDevices, setReservedDevices] = useState<getDevicesQuery['devices']>(initialContext.reservedDevices);

  useEffect(() => {
    if (!selectedDeviceId && selectedDevice) {
      return setSelectedDevice(initialContext.selectedDevice);
    }
    if (selectedDeviceId && reservedDevices.find((e) => e.id === selectedDeviceId)) {
      setSelectedDevice(reservedDevices.find((e) => e.id === selectedDeviceId));
    }
  }, [selectedDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UseSelectedDeviceContext.Provider
      value={{
        setSelectedDeviceId,
        selectedDeviceId,
        selectedDevice,
        setSelectedDevice,
        reservedDevices,
        setReservedDevices,
      }}
    >
      {children}
    </UseSelectedDeviceContext.Provider>
  );
};

export default SelectedDeviceProvider;
